import { HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, tap } from "rxjs";
import { API_ENDPOINTS } from "../constants";
import { ApiService } from "./api.service";
import { OrganizationType } from "../types/app.step.type";

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginWithMagicCodeResponse {
  pending_authentication_token?: string;
  access_token?: string;
  session_id?: string;
  organizations?: OrganizationType[];
  login_hint?: string
}

export interface LoginWithMagicCodeRequest {
  email: string;
  code: string;
  redirectUri?: string;
}

export interface LoginWithOrganizationsSelectionRequest {
  pending_authentication_token: string;
  organization_id?: string;
  login_hint?: string
}

export interface UsernameOrEmailCheckResponse {
  userExists: boolean
  isUserMigrated: boolean
  appTypeId: number
  username: string
  email: string
}

export interface MigrationStatusRequest {
  email?: string;
  username?: string;
  password?: string;
}

export interface MigrationStatusResponse {
  userExists?: boolean;
  usernameExists?: boolean;
  isUserMigrated?: boolean;
  email?: string | null;
  username?: string | null;
}

export interface MigrationProcessRequest {
  confirmPassword: string;
  email: string;
  newPassword: string;
  oldPassword?: string;
  username?: string;
}

export interface HttpErrorResponse {
  status: number;
  statusText: string;
  url: string;
  ok: boolean;
  name: string;
  message: string;
  error: {
    message: string;
    context: {
      message: string;
      code: string;
      errors: {
        message: string;
        code: string;
      }[];
    };
  };
}

export interface AuthWithMagicAuthRequest {
  client_id: string;
  client_secret: string;
  grant_type: "urn:workos:oauth:grant-type:magic-auth:code";
  code: string;
  email: string;
}

interface WorkOSUser {
  object: string;
  id: string;
  email: string;
  email_verified: boolean;
  first_name: string;
  last_name: string;
  profile_picture_url: string | null;
  last_sign_in_at: string;
  created_at: string;
  updated_at: string;
  external_id: string | null;
}

export interface ExchangeCodeResponse {
  organization_id: string;
  access_token: string;
  refresh_token: string;
  session_id: string;
  authentication_method: string;
  user: WorkOSUser;
  app_type_id: number
}

export interface SignOutBySessionIdParams {
  sessionId: string
}

@Injectable({
  providedIn: "root"
})
export class AuthService {
  constructor(private apiService: ApiService) { }

  checkUsernameOrEmail(usernameOrEmail: string): Observable<UsernameOrEmailCheckResponse> {
    return this.apiService.post<UsernameOrEmailCheckResponse>(API_ENDPOINTS.AUTH.MIGRATION_STATUS, {
      email: usernameOrEmail
    });
  }

  checkLogin(username: string, password: string): Observable<MigrationStatusResponse> {
    return this.apiService.post<MigrationStatusResponse>(API_ENDPOINTS.AUTH.MIGRATION_STATUS, {
      username,
      password
    });
  }

  checkMigrationStatus(params: MigrationStatusRequest): Observable<MigrationStatusResponse> {
    return this.apiService.post<MigrationStatusResponse>(
      API_ENDPOINTS.AUTH.MIGRATION_STATUS,
      params
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  migrate(params: MigrationProcessRequest): Observable<any> {
    return this.apiService.post(API_ENDPOINTS.AUTH.MIGRATION_PROCESS, params);
  }

  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.apiService.post<LoginResponse>(API_ENDPOINTS.AUTH.LOGIN, credentials).pipe(
      tap((response) => {
        localStorage.setItem("token", response.token);
        localStorage.setItem("user", JSON.stringify(response.user));
      })
    );
  }

  loginWithMagicCode(credentials: LoginWithMagicCodeRequest): Observable<LoginWithMagicCodeResponse> {
    return this.apiService.post<LoginWithMagicCodeResponse>(API_ENDPOINTS.AUTH.LOGIN_WITH_MAGIC_CODE, credentials)
  }

  loginOrganizationsSelection(credentials: LoginWithOrganizationsSelectionRequest): Observable<LoginWithMagicCodeResponse> {
    return this.apiService.post<LoginWithMagicCodeResponse>(API_ENDPOINTS.AUTH.LOGIN_WITH_ORGANIZATIONS_SELECTION, credentials)
  }

  logout(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem("token");
  }

  getToken(): string | null {
    return localStorage.getItem("token");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getUser(): any {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  }

  forgotPassword(email: string) {
    const headers = new HttpHeaders({
      "Content-Type": "application/json"
    });

    return this.apiService.post(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      {
        emailAddress: email
      },
      {
        headers
      }
    );
  }

  exchangeCode(code: string): Observable<ExchangeCodeResponse> {
    return this.apiService.get<ExchangeCodeResponse>(API_ENDPOINTS.AUTH.EXCHANGE_CODE, {
      code
    });
  }

  signOutBySessionId(params: SignOutBySessionIdParams): Observable<void> {
    return this.apiService.get(API_ENDPOINTS.AUTH.LOGOUT_SESSION, params);
  }
}
